import type { DataTableFilterMeta } from 'primereact/datatable';
import type { AnalysisJsonObject } from '@/types/analysisRecipe.types';

export interface ConditionLeaf {
  type: 'leaf';
  field: string;
  operator: string;
  value: string;
}

export interface RelatedLeaf {
  type: 'related';
  recordLabel: string;
  quantifier: 'has' | 'hasNone';
  where?: ConditionGroup | null;
  relationship?: string;
  direction?: 'in' | 'out' | 'both';
}

export interface ConditionGroup {
  type: 'group';
  joiner: 'all' | 'any';
  conditions: ConditionNode[];
}

export type ConditionNode = ConditionLeaf | RelatedLeaf | ConditionGroup;

/**
 * Operators the compiler coerces to a number (`predicateValue` in
 * `analysisRecipeCompiler.helpers.ts`). Equality is deliberately absent: whether
 * `is 100` means the number or the string depends on the property's schema kind,
 * which the compiler does not receive, so coercing it here would break a text
 * field holding a numeric-looking code.
 */
const NUMERIC_COMPARISON_OPERATORS = ['gt', 'gte', 'lt', 'lte', 'absGt'];

export const CONDITION_OPERATORS = [
  { value: 'is', label: 'is', needsValue: true },
  { value: 'isNot', label: 'is not', needsValue: true },
  { value: 'contains', label: 'contains', needsValue: true },
  { value: 'notContains', label: 'does not contain', needsValue: true },
  { value: 'startsWith', label: 'starts with', needsValue: true },
  { value: 'notStartsWith', label: 'does not start with', needsValue: true },
  { value: 'endsWith', label: 'ends with', needsValue: true },
  { value: 'isOneOf', label: 'is one of', needsValue: true },
  { value: 'gt', label: 'greater than', needsValue: true },
  { value: 'gte', label: 'at least', needsValue: true },
  { value: 'lt', label: 'less than', needsValue: true },
  { value: 'lte', label: 'at most', needsValue: true },
  { value: 'absGt', label: 'differs by more than', needsValue: true },
  { value: 'isBlank', label: 'is blank', needsValue: false },
  { value: 'notBlank', label: 'is not blank', needsValue: false },
] as const;

export const emptyGroup = (
  joiner: 'all' | 'any' = 'all',
): ConditionGroup => ({ type: 'group', joiner, conditions: [] });

export const emptyLeaf = (field = ''): ConditionLeaf => ({
  type: 'leaf',
  field,
  operator: 'is',
  value: '',
});

const operatorLabel = (operator: string): string =>
  CONDITION_OPERATORS.find((candidate) => candidate.value === operator)?.label ??
  operator;

/**
 * PrimeReact match modes that mean the same thing as a condition operator under
 * a different name. Column filters speak match modes, condition trees speak
 * operators, and a chip built from either must read the same way.
 */
const MATCH_MODE_ALIASES: Record<string, string> = {
  equals: 'is',
  notEquals: 'isNot',
};

/**
 * An operator in the author's words, for chips built outside a condition tree —
 * a promoted control, for instance, whose operator came from a column filter.
 * Printing the raw token gave chips like `rows · count gt 100`.
 */
export const describeConditionOperator = (operator: string): string =>
  operatorLabel(MATCH_MODE_ALIASES[operator] ?? operator);

/**
 * The absence of a display map, stated rather than defaulted.
 *
 * Use it where a field genuinely has no friendly name to resolve — a raw node
 * property is already readable. Never reach for it to silence the type error on
 * a surface that shows Made columns: those fields are encoded keys.
 */
export const rawFieldLabel = (field: string): string => field;

/**
 * The condition in the author's words.
 *
 * `labelFor` maps a field to its display label. A computed column's field is an
 * encoded workset key, so without it a sentence built over one reads as
 * `analysis__00007300…  is  company` wherever it is shown.
 *
 * It is REQUIRED, and deliberately has no identity default. The default was
 * how this went wrong twice: a caller that simply forgot got an encoded key on
 * screen and nothing complained. A caller with no display map says so by
 * passing `rawFieldLabel`, which is greppable; forgetting is now a type error.
 */
export function conditionSentence(
  node: ConditionNode,
  labelFor: (field: string) => string,
): string {
  if (node.type === 'leaf') {
    const operator = CONDITION_OPERATORS.find(
      (candidate) => candidate.value === node.operator,
    );
    const field = node.field ? labelFor(node.field) : '…';
    return operator?.needsValue === false
      ? `${field} ${operatorLabel(node.operator)}`
      : `${field} ${operatorLabel(node.operator)} ${node.value || '…'}`;
  }
  if (node.type === 'related') {
    const base =
      node.quantifier === 'has'
        ? `has at least one ${node.recordLabel}`
        : `has no ${node.recordLabel} records`;
    return node.where?.conditions.length
      ? `${base} where ${conditionSentence(node.where, labelFor)}`
      : base;
  }
  return node.conditions
    .map((child) =>
      child.type === 'group'
        ? `(${conditionSentence(child, labelFor)})`
        : conditionSentence(child, labelFor),
    )
    .join(node.joiner === 'all' ? ' and ' : ' or ');
}

export function conditionFields(node: ConditionNode): string[] {
  if (node.type === 'leaf') return node.field ? [node.field] : [];
  if (node.type === 'related') {
    return node.where ? conditionFields(node.where) : [];
  }
  return [...new Set(node.conditions.flatMap(conditionFields))];
}

export const relatedLeavesOf = (
  group: ConditionGroup,
): Array<{ leaf: RelatedLeaf; index: number }> =>
  group.conditions.flatMap((node, index) =>
    node.type === 'related' ? [{ leaf: node, index }] : [],
  );

export const withoutRelatedLeaves = (
  group: ConditionGroup,
): ConditionGroup => ({
  ...group,
  conditions: group.conditions.filter((node) => node.type !== 'related'),
});

/**
 * Why a tree cannot be committed yet, or `null` when every leaf is complete.
 *
 * An incomplete leaf is not a harmless no-op: `field: ''` or `value: ''`
 * compiles into a predicate the backend evaluates literally, so a half-typed
 * row would silently filter everything away. Operators that take no value
 * (`is blank`, `is not blank`) are complete with an empty value.
 *
 * `labelFor` maps a field to its display label, same as `conditionSentence` —
 * a computed column's field is an encoded workset key, so without it this
 * message read `Enter a value for "analysis__…" is`. Required for the same
 * reason; pass `rawFieldLabel` when there is genuinely no map.
 */
export function conditionTreeIssue(
  node: ConditionNode,
  labelFor: (field: string) => string,
): string | null {
  if (node.type === 'leaf') {
    if (!node.field.trim()) return 'Choose a field for every condition.';
    const operator = CONDITION_OPERATORS.find(
      (candidate) => candidate.value === node.operator,
    );
    if (!operator) return `“${node.operator}” is not a supported operator.`;
    const field = labelFor(node.field);
    if (operator.needsValue && !node.value.trim()) {
      return `Enter a value for “${field} ${operator.label}”.`;
    }
    // A comparison compiles to a numeric predicate, so non-numeric text would
    // reach the engine as a string and quietly match no rows instead of
    // erroring. Refuse it here, where the author can still see why.
    if (NUMERIC_COMPARISON_OPERATORS.includes(node.operator)) {
      const numeric = Number(node.value.trim());
      if (!Number.isFinite(numeric)) {
        return `“${field} ${operator.label}” needs a number.`;
      }
    }
    return null;
  }
  if (node.type === 'related') {
    if (!node.recordLabel.trim()) {
      return 'Choose a connected record for every existence condition.';
    }
    return node.where ? conditionTreeIssue(node.where, labelFor) : null;
  }
  if (node.conditions.length === 0) return 'Add at least one condition.';
  for (const child of node.conditions) {
    const issue = conditionTreeIssue(child, labelFor);
    if (issue) return issue;
  }
  return null;
}

const META_TO_OPERATOR: Record<string, string> = {
  equals: 'is',
  notEquals: 'isNot',
  contains: 'contains',
  notContains: 'notContains',
  startsWith: 'startsWith',
  endsWith: 'endsWith',
  gt: 'gt',
  gte: 'gte',
  lt: 'lt',
  lte: 'lte',
};

export function treeFromFilterMeta(meta: DataTableFilterMeta): ConditionGroup {
  const group = emptyGroup('all');
  Object.entries(meta ?? {}).forEach(([field, value]) => {
    const typed = value as {
      constraints?: Array<{ value?: unknown; matchMode?: string }>;
      matchMode?: string;
      value?: unknown;
    };
    const constraint = typed.constraints?.[0] ?? typed;
    if (
      constraint.value === null ||
      constraint.value === undefined ||
      String(constraint.value).trim() === ''
    ) {
      return;
    }
    group.conditions.push({
      type: 'leaf',
      field,
      operator:
        META_TO_OPERATOR[String(constraint.matchMode ?? 'contains')] ??
        'contains',
      value: String(constraint.value),
    });
  });
  return group;
}

export const conditionGroupToJson = (
  group: ConditionGroup,
): AnalysisJsonObject => group as unknown as AnalysisJsonObject;

export const conditionGroupFromJson = (
  value: AnalysisJsonObject | null,
): ConditionGroup =>
  value?.type === 'group' &&
  (value.joiner === 'all' || value.joiner === 'any') &&
  Array.isArray(value.conditions)
    ? (value as unknown as ConditionGroup)
    : emptyGroup();
